import React, { useEffect, useCallback, useRef } from 'react';
import { ThemeProvider } from 'styled-components';
import styled from 'styled-components';
import { GlobalStyles } from './styles/GlobalStyles';
import { theme } from './styles/theme';
import {
  HeaderSection,
  HeroSection,
  AboutSection,
  TechnologiesSection,
  ProjectsSection,
  ContactSection,
  FooterSection
} from './sections';

const ScrollProgressBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 0%;
  height: 3px;
  background: linear-gradient(90deg, ${({ theme }) => theme.colors.navyBlue}, ${({ theme }) => theme.colors.lightNavy});
  z-index: 1002;
  transition: none;
  box-shadow: 0 0 10px rgba(30, 58, 138, 0.3);
  transform-origin: left;
  will-change: width;
`;

const App: React.FC = () => {
  const progressBarRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>();

  const updateScrollProgress = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight > 0 && progressBarRef.current) {
      const progress = Math.max(0, Math.min((scrollTop / docHeight) * 100, 100));
      progressBarRef.current.style.width = `${progress}%`;
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(updateScrollProgress);
    };

    updateScrollProgress();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [updateScrollProgress]);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <div className="App">
        <ScrollProgressBar ref={progressBarRef} />

        {/* Skip to main content link for keyboard users */}
        <a 
          href="#main-content" 
          className="skip-link"
          style={{
            position: 'absolute',
            top: '-40px',
            left: '6px',
            background: '#1e3a8a',
            color: 'white',
            padding: '8px',
            textDecoration: 'none',
            borderRadius: '4px',
            zIndex: 1001,
            transition: 'top 0.3s',
          }}
          onFocus={(e) => {
            e.target.style.top = '6px';
          }}
          onBlur={(e) => {
            e.target.style.top = '-40px';
          }}
        >
          Skip to main content
        </a>

        <HeaderSection />
        
        <main id="main-content" role="main" aria-label="Main content">
          <HeroSection />
          <AboutSection />
          <TechnologiesSection />
          <ProjectsSection />
          <ContactSection />
        </main>
        
        <FooterSection />
      </div>
    </ThemeProvider>
  );
};

export default App; 